import { useMemo, useState } from 'react';
import { isSameMonth } from 'date-fns';
import Calendar from 'src/app/components/kit/calendar/Calendar';
import Footer from 'src/app/components/ui/footer/Footer';
import { useAppContext } from 'src/app/state';
import styles from './DataPage.module.css';
import Text from 'src/app/components/kit/text/Text';
import Button from 'src/app/components/kit/button/Button';
import { LuDownload } from 'react-icons/lu';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import { getCurrentDay } from 'src/app/utils/calendar.utils';
import { ParsedDay } from 'src/types/date-time.types';
import ActionButton from 'src/app/components/kit/action-button/ActionButton';
import { ActionType } from 'src/app/state/action-type.enum';
import Table from 'antd/lib/table';

const DeleteDataButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button
      type='delete'
      width='full-width'
      onClick={onClick}
      style={{ width: '100%', marginLeft: 0 }}
    >
      Delete Data
    </Button>
  );
};

const DataPage = () => {
  const {
    state: { migraineAttacks },
    dispatch,
  } = useAppContext();
  const today: ParsedDay = useMemo(() => getCurrentDay(new Date()), []);
  const [month, setMonth] = useState(today.month);
  const migraineDays: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => record.isMigraineDay)
        .map((record) => record.date!),
    [migraineAttacks]
  );
  const vyduraEveryOtherDayDates: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => record.vyduraEveryOtherDay === true)
        .map((record) => record.date!),
    [migraineAttacks]
  );
  const vyduraPerAttackDays: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => record.vyduraPerAttack === true)
        .map((record) => record.date!),
    [migraineAttacks]
  );
  const countDaysOfTheSameMonth = (days: Date[], month: string) => {
    const monthDay = new Date(`01 ${month} ${today.year}`);
    const daysOfTheMonth = days.filter((day) => isSameMonth(monthDay, day));
    return daysOfTheMonth ? daysOfTheMonth.length : 0;
  };
  const onDateSelect = (day: Date) => undefined;
  const handleDeleteData = () => {
    dispatch({
      type: ActionType.SET_DOCTOR_APPOINTMENT,
      payload: { doctorAppointment: undefined },
    });
    dispatch({
      type: ActionType.SET_VYDURA_REMINDER,
      payload: { vyduraReminder: undefined },
    });
    dispatch({
      type: ActionType.SET_MIGRAINE_ATTACK,
      payload: { migraineAttacks: [] },
    });
  };
  const onReportDownload = () =>
    window.open(
      'https://www.figma.com/design/r6T1iDZXW06e7w9lLxxHOH/Reports---Review?node-id=1403-12388',
      '_blank'
    );

  return (
    <div>
      <div className={styles.header}>
        <Text weight='bold' tag='h2' size='m-large'>
          Data
        </Text>
        <Button
          type='primary'
          width='content-width'
          onClick={onReportDownload}
          style={{
            display: 'flex',
            alignItems: 'center',
            fontSize: '14px',
            padding: '0 20px',
            height: '40px',
          }}
        >
          <LuDownload />
          &nbsp;&nbsp;&nbsp;Download Report
        </Button>
      </div>
      <div className={styles.content}>
        <Text weight='bold' tag='h3' size='medium'>
          Calendar view
        </Text>
        <Calendar
          onDateSelect={onDateSelect}
          showCurrentDate={false}
          migraineDates={migraineDays}
          vyduraPerAttacksDates={vyduraPerAttackDays}
          vyduraEveryDayDates={vyduraEveryOtherDayDates}
          onMonthChange={setMonth}
        />
        <Text weight='bold'>Legend</Text>
        <Row className={styles.legend}>
          <Col flex='40px'>
            <div className={styles.migraineIcon} />
          </Col>
          <Col flex='auto'>
            <Text
              tag='span'
              color='gray'
              style={{ opacity: 0.8, lineHeight: '21px', fontSize: '14px' }}
            >
              Migraine day
            </Text>
          </Col>
        </Row>
        <Row className={styles.legend}>
          <Col flex='40px'>
            <div className={styles.vyduraPerAttackIcon} />
          </Col>
          <Col flex='auto'>
            <Text
              size='x-small'
              tag='span'
              color='gray'
              style={{ opacity: 0.8, lineHeight: '21px', fontSize: '14px' }}
            >
              Vydura (per attack)
            </Text>
          </Col>
        </Row>
        <Row className={styles.legend}>
          <Col flex='40px'>
            <div className={styles.vyduraEveryOtherDayIcon} />
          </Col>
          <Col flex='auto'>
            <Text
              size='x-small'
              tag='span'
              color='gray'
              style={{ opacity: 0.8, lineHeight: '21px', fontSize: '14px' }}
            >
              Vydura (every other day)
            </Text>
          </Col>
        </Row>
        <div className={styles.counter}>
          <Text weight='bold' tag='h1' size='x-large'>
            {countDaysOfTheSameMonth(migraineDays, month)}{' '}
            {`${countDaysOfTheSameMonth(migraineDays, month) === 1 ? 'day' : 'days'}`}
          </Text>
          <Text color='gray' style={{ opacity: 0.8, padding: '6px' }}>
            Migraine attack in {month}
          </Text>
        </div>
        <Text weight='bold' tag='h3' size='medium'>
          Vydura 75mg medication
        </Text>
        <Table
          pagination={false}
          rowHoverable={false}
          columns={[
            {
              title: (
                <Text weight='bold' tag='span' style={{ fontSize: '14px' }}>
                  Medication
                </Text>
              ),
              dataIndex: 'medication',
              key: 'medication',
            },
            {
              title: (
                <Text weight='bold' tag='span' style={{ fontSize: '14px' }}>
                  Medication days
                </Text>
              ),
              dataIndex: 'days',
              key: 'days',
            },
          ]}
          dataSource={[
            {
              key: '1',
              medication: (
                <>
                  <Text
                    tag='span'
                    weight='semi-bold'
                    style={{ fontSize: '14px' }}
                  >
                    Vydura 75mg
                  </Text>
                  <Text
                    tag='span'
                    size='x-small'
                    color='gray'
                    style={{ opacity: 0.8, display: 'block' }}
                  >
                    Per attack
                  </Text>
                </>
              ),
              days: (
                <Text style={{ fontSize: '14px' }}>
                  {countDaysOfTheSameMonth(vyduraPerAttackDays, month)}
                  {`${countDaysOfTheSameMonth(vyduraPerAttackDays, month) === 1 ? ' day' : ' days'}`}
                </Text>
              ),
            },
            {
              key: '2',
              medication: (
                <>
                  <Text
                    tag='span'
                    weight='semi-bold'
                    style={{ fontSize: '14px' }}
                  >
                    Vydura 75mg
                  </Text>
                  <Text
                    tag='span'
                    size='x-small'
                    color='gray'
                    style={{ opacity: 0.8, display: 'block' }}
                  >
                    Every other day
                  </Text>
                </>
              ),
              days: (
                <Text style={{ fontSize: '14px' }}>
                  {countDaysOfTheSameMonth(vyduraEveryOtherDayDates, month)}
                  {`${countDaysOfTheSameMonth(vyduraEveryOtherDayDates, month) === 1 ? ' day' : ' days'}`}
                </Text>
              ),
            },
          ]}
        />
        <div className={styles.counter}>
          <Text weight='bold' tag='h1' size='x-large'>
            {countDaysOfTheSameMonth(
              [...vyduraPerAttackDays, ...vyduraEveryOtherDayDates],
              month
            )}{' '}
            {`${countDaysOfTheSameMonth([...vyduraPerAttackDays, ...vyduraEveryOtherDayDates], month) === 1 ? 'day' : 'days'}`}
          </Text>
          <Text color='gray' style={{ opacity: 0.8, padding: '6px' }}>
            Vydura 75mg in {month}
          </Text>
        </div>
        <Text weight='bold' tag='h3' size='medium'>
          Delete my data
        </Text>
        <Text color='gray' style={{ paddingTop: 0 }}>
          By selecting this option, all your data, preferences, and history will
          be permanently deleted. This action cannot be undone.
        </Text>
        <ActionButton
          onConfirm={handleDeleteData}
          MainButton={DeleteDataButton}
          popUpText={
            <Text color='gray'>
              By deleting your account, all your personal data, preferences, and
              records will be permanently removed. This action cannot be undone.
              Are you sure you want to continue?
            </Text>
          }
          popUpHeader='Delete data'
          hasFooter={true}
          skipButtonText='Cancel'
          confirmButtonText='Delete'
        />
      </div>
      <Footer />
    </div>
  );
};

export default DataPage;
